import React, { useMemo } from "react";
import { View, StyleSheet, Pressable, PressableStateCallbackType } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CheckedBadge from "../../svgs/common/CheckedBadge";
import { HeadCircuitIcon } from "@phosphor-icons/react";
import { useAppConfig } from "../../../AppConfigProvider";
import { ActivityState, AssessmentSubmissionStatus } from "../../../graphql/generated/graphql";
import DueDatePill from "../Shared/DueDatePill";
import ScheduledPill from "../Shared/ScheduledPill";

interface AssessmentCardProps {
  onButtonPress: () => void;
  grade: number;
  status?: AssessmentSubmissionStatus | null;
  isEvaluationPending: boolean;
  onPress: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
  isHovered: boolean;
  maximumPoints: number;
  dueDate?: string | Date | null;
  activityState: ActivityState | undefined;
  scheduledDate: string | Date | undefined;
}

interface AssessmentCardContentProps {
  onButtonPress: () => void;
  status?: AssessmentSubmissionStatus | null;
  grade: number;
  isEvaluationPending: boolean;
  maximumPoints: number;
  isModuleLocked: boolean;
  scheduledDate: string | Date | null;
  dueDate?: string | Date | null;
}

function AssessmentCardContent({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
  maximumPoints,
  isModuleLocked,
  scheduledDate,
  dueDate,
}: AssessmentCardContentProps) {
  const { primaryColor } = useAppConfig();
  const hasGrade = grade !== null && grade !== undefined;
  return (
    <>
      <View style={styles.resourceItemLeft}>
        <View style={styles.textContainer}>
          <HeadCircuitIcon fill={primaryColor} size={24} />
          <CustomText
            style={isModuleLocked && styles.cardContainerTitleLocked}
            text="Lesson Assessment"
            weight="bold"
            aria-level="3"
            role="heading"
          />
        </View>
        {!isModuleLocked && <DueDatePill dueDate={dueDate} />}
        {isModuleLocked && <ScheduledPill scheduledDate={scheduledDate} />}
      </View>
      {!isModuleLocked &&
        (status === AssessmentSubmissionStatus.Completed ? (
          <View style={styles.buttonsContainer}>
            <View style={styles.gradeContainer}>
              {hasGrade && (
                <View style={styles.gradeTag}>
                  <CustomText
                    text={`${grade}/${maximumPoints}`}
                    useTranslationText={false}
                    size="s"
                    weight="ultraBold"
                    style={{ color: "#fff" }}
                  />
                </View>
              )}
              {isEvaluationPending && !hasGrade && (
                <View style={styles.gradeTagUngraded}>
                  <CustomText
                    text="Evaluation Pending"
                    useTranslationText={false}
                    size="s"
                    weight="ultraBold"
                    style={{ color: "#fff" }}
                  />
                </View>
              )}
              {!isEvaluationPending && (
                <CustomButton
                  styleType="primary"
                  text="Retake"
                  textStyle={styles.buttonText}
                  onPress={onButtonPress}
                  style={{ backgroundColor: primaryColor }}
                />
              )}
              <CheckedBadge />
            </View>
          </View>
        ) : isEvaluationPending ? (
          <View style={styles.gradeTagUngraded}>
            <CustomText
              text="Evaluation Pending"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={{ color: "#fff" }}
            />
          </View>
        ) : (
          <View style={styles.buttonsContainer}>
            <CustomButton
              styleType="primary"
              text={`${status === AssessmentSubmissionStatus.Running ? "Resume" : "Begin"} Assessment`}
              textStyle={styles.buttonText}
              onPress={onButtonPress}
              style={{ backgroundColor: primaryColor }}
            />
          </View>
        ))}
    </>
  );
}

function AssessmentCard({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
  onPress = () => {},
  onHoverIn = () => {},
  onHoverOut = () => {},
  isHovered = false,
  maximumPoints,
  activityState,
  scheduledDate,
  dueDate,
}: AssessmentCardProps) {
  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  // hide if draft
  if (activityState === ActivityState.Draft) {
    return null;
  }

  if (
    status !== AssessmentSubmissionStatus.Completed ||
    (status === AssessmentSubmissionStatus.Completed && isEvaluationPending)
  ) {
    return (
      <View style={styles.cardContainer}>
        <AssessmentCardContent
          onButtonPress={onButtonPress}
          status={status}
          grade={grade}
          isEvaluationPending={isEvaluationPending}
          maximumPoints={maximumPoints}
          isModuleLocked={isModuleLocked}
          scheduledDate={scheduledDate}
          dueDate={dueDate}
        />
      </View>
    );
  }

  return (
    <Pressable
      onPress={isModuleLocked ? undefined : onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityLabel={`Open Assessment card`}
      accessibilityRole="button"
      style={(state: PressableStateCallbackType & { focused?: boolean }) => {
        const { pressed, focused } = state;
        const isFocused = focused ?? false;

        return [
          styles.cardContainer,
          !isModuleLocked && isHovered && styles.cardContainerHovered,
          !isModuleLocked && isFocused && styles.cardContainerFocused,
          pressed && styles.cardContainerPressed,
        ];
      }}>
      <AssessmentCardContent
        onButtonPress={onButtonPress}
        status={status}
        grade={grade}
        isEvaluationPending={isEvaluationPending}
        maximumPoints={maximumPoints}
        isModuleLocked={isModuleLocked}
        scheduledDate={scheduledDate}
        dueDate={dueDate}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    // @ts-expect-error - Web CSS property
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  cardContainerTitleLocked: {
    opacity: 0.5,
  },
  resourceItemLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  textContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 8,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: "700",
  },
  gradeContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
  },
  gradeTag: {
    padding: 8,
    backgroundColor: "#0084FF",
    borderRadius: 100,
  },
  gradeTagUngraded: {
    padding: 8,
    backgroundColor: "#D0D5DD",
    borderRadius: 100,
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  cardContainerHovered: {
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  cardContainerFocused: {
    outlineWidth: 2,
    outlineColor: "#3E68FE",
    outlineStyle: "solid",
    boxShadow: "0 0 0 2px rgba(62, 104, 254, 0.2)",
  },
  cardContainerPressed: {
    backgroundColor: "#F5F5F5",
  },
});

export default AssessmentCard;
